import { ApolloClient, createHttpLink, InMemoryCache, Observable, from } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'

const httpLink = createHttpLink({
  uri: `${process.env.REACT_APP_URL_API_NEST}/graphql`,
  fetch,
})

const authLink = setContext(async (_, { headers }) => {
  if (headers?.authorization) {
    return { headers };
  }

  const token = localStorage.getItem('@AcordeOn:token');

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  }
})

// ========================================
// Controle de refresh token para Apollo (anti-loop)
// ========================================
let isRefreshingApollo = false;
let pendingRequests = [];

const VERIFY_TOKEN_MUTATION = `
  mutation VerifyToken {
    verifyToken {
      __typename
      ... on LoginSuccessResponse {
        accessToken
        refreshToken
      }
      ... on LoginErrorResponse {
        message
      }
    }
  }
`;

const LOGOUT_MUTATION = `
  mutation Logout {
    logout
  }
`;

const resolvePendingRequests = () => {
  pendingRequests.forEach(callback => callback());
  pendingRequests = [];
};

const callLogoutMutation = (token) => {
  if (!token) return;
  // Chama logout via fetch direto para não passar pelo errorLink
  fetch(`${process.env.REACT_APP_URL_API_NEST}/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query: LOGOUT_MUTATION }),
  }).catch(() => {
    // Ignora erros de logout - o importante é limpar o localStorage
  });
};

const forceLogout = () => {
  const token = localStorage.getItem('@AcordeOn:token');
  callLogoutMutation(token);

  localStorage.removeItem('@AcordeOn:token');
  localStorage.removeItem('@AcordeOn:refreshToken');
  localStorage.removeItem('@AcordeOn:user');
  if (
    !window.location.pathname.includes('/login') &&
    !window.location.pathname.includes('/cadastro') &&
    !window.location.pathname.includes('/redefinir-senha')
  ) {
    window.location.href = '/login';
  }
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  // Verifica se é erro de autenticação (UNAUTHENTICATED ou 401)
  const isAuthError =
    graphQLErrors?.some(
      (err) =>
        err.extensions?.code === 'UNAUTHENTICATED' ||
        err.extensions?.code === 'FORBIDDEN' ||
        err.message?.toLowerCase().includes('unauthorized') ||
        err.message?.toLowerCase().includes('jwt expired') ||
        (err.message?.toLowerCase().includes('invalid') && err.message?.toLowerCase().includes('token'))
    ) ||
    (networkError && 'statusCode' in networkError && networkError.statusCode === 401);

  if (!isAuthError) return;

  // Não tenta refresh para a própria mutation de verifyToken (evita loop)
  const operationName = operation.operationName;
  if (operationName === 'VerifyToken') {
    forceLogout();
    return;
  }

  if (isRefreshingApollo) {
    // Se já está fazendo refresh, enfileira e espera
    return new Observable((observer) => {
      pendingRequests.push(() => {
        // Atualiza o header com o novo token
        const newToken = localStorage.getItem('@AcordeOn:token');
        const oldHeaders = operation.getContext().headers;
        operation.setContext({
          headers: {
            ...oldHeaders,
            authorization: newToken ? `Bearer ${newToken}` : '',
          },
        });
        // Refaz a operação
        forward(operation).subscribe(observer);
      });
    });
  }

  isRefreshingApollo = true;

  return new Observable((observer) => {
    const refreshToken = localStorage.getItem('@AcordeOn:refreshToken');

    if (!refreshToken) {
      isRefreshingApollo = false;
      forceLogout();
      observer.error(new Error('No refresh token'));
      return;
    }

    // Faz a chamada de refresh diretamente via fetch para evitar
    // passar pelo errorLink novamente (evita loop)
    fetch(`${process.env.REACT_APP_URL_API_NEST}/graphql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${refreshToken}`,
      },
      body: JSON.stringify({
        operationName: 'VerifyToken',
        query: VERIFY_TOKEN_MUTATION,
        variables: {},
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        const verifyResult = result?.data?.verifyToken;

        // Verifica se o refresh falhou:
        // - resposta vazia
        // - tipo explícito de erro
        // - ausência de accessToken (caso __typename não venha na resposta)
        if (
          !verifyResult ||
          verifyResult.__typename === 'LoginErrorResponse' ||
          !verifyResult.accessToken
        ) {
          throw new Error(
            verifyResult?.message || 'Token refresh failed'
          );
        }

        // Sucesso - atualiza tokens
        localStorage.setItem('@AcordeOn:token', verifyResult.accessToken);
        localStorage.setItem('@AcordeOn:refreshToken', verifyResult.refreshToken);

        // Atualiza o header da operação original
        const oldHeaders = operation.getContext().headers;
        operation.setContext({
          headers: {
            ...oldHeaders,
            authorization: `Bearer ${verifyResult.accessToken}`,
          },
        });

        isRefreshingApollo = false;
        resolvePendingRequests();

        // Refaz a operação original
        forward(operation).subscribe(observer);
      })
      .catch((err) => {
        isRefreshingApollo = false;
        pendingRequests = [];
        console.error('Apollo token refresh failed, logging out:', err.message);
        forceLogout();
        observer.error(err);
      });
  });
});

const cache = new InMemoryCache({})

export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache,
  defaultOptions: {},
})
